import { useMemo } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";

import type { ArchitectureComponent } from "@/presentation/data/architecture-components";
import { DOMAIN_COLOR } from "@/presentation/data/domains";
import type { ProductRow } from "@/infrastructure/supabase/database.types";

interface ComponentPickerRowProps {
  component: ArchitectureComponent;
  products: ProductRow[];
  chosenProductId: string | null;
  score: number | null;
  isDealbreaker: boolean;
  /** Effective criticality (1..5), already resolved from override → baseline. */
  criticality: number;
  onPick: (productId: string | null) => void;
  onCriticalityChange: (value: number) => void;
}

const CRITICALITY_VALUES = [1, 2, 3, 4, 5] as const;

const KIND_COLOR = {
  "off-the-shelf": "oklch(0.55 0.17 190)",
  "build-on-top": "oklch(0.55 0.16 260)",
  "from-scratch": "oklch(0.62 0.16 50)",
} as const;

const KIND_LABEL = {
  "off-the-shelf": "Off-the-shelf",
  "build-on-top": "Build on top",
  "from-scratch": "From scratch",
} as const;

export function ComponentPickerRow({
  component,
  products,
  chosenProductId,
  score,
  isDealbreaker,
  criticality,
  onPick,
  onCriticalityChange,
}: ComponentPickerRowProps) {
  const eligibleProducts = useMemo(() => {
    return products.filter(
      (p) => p.applies_to_components.length === 0 || p.applies_to_components.includes(component.id)
    );
  }, [products, component.id]);

  const chosen = chosenProductId ? products.find((p) => p.id === chosenProductId) : null;
  const accent = chosen ? KIND_COLOR[chosen.kind] : "oklch(0.85 0.005 240)";
  const domainColor = DOMAIN_COLOR[component.domain];

  return (
    <div
      className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 transition-colors"
      style={{
        borderLeft: `3px solid ${chosen ? accent : domainColor}`,
        background: isDealbreaker ? "oklch(0.97 0.04 25 / 0.55)" : undefined,
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[12.5px] leading-tight font-medium">{component.name}</span>
        <span className="text-foreground/55 text-[10.5px]">{component.role}</span>
      </div>

      <Select value={String(criticality)} onValueChange={(v) => onCriticalityChange(Number(v))}>
        <SelectTrigger
          className="font-data h-6 w-[3.25rem] shrink-0 gap-0.5 px-1.5 text-[10px] font-semibold tabular-nums"
          title="Component criticality (1..5) — weights this component in the composite. Editing applies to all candidates."
        >
          <span>×{criticality}</span>
        </SelectTrigger>
        <SelectContent>
          {CRITICALITY_VALUES.map((v) => (
            <SelectItem key={v} value={String(v)}>
              <span className="font-data text-[12px] tabular-nums">×{v}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-56 shrink-0">
        <Select
          value={chosenProductId ?? "tbd"}
          onValueChange={(v) => onPick(v === "tbd" ? null : v)}
        >
          <SelectTrigger className="h-8 text-[12px]">
            <SelectValue placeholder="TBD" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tbd">
              <span className="text-muted-foreground italic">TBD (no choice)</span>
            </SelectItem>
            {eligibleProducts.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                <span className="inline-flex items-center gap-2">
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: KIND_COLOR[p.kind] }}
                  />
                  <span className="text-[12.5px]">{p.name}</span>
                  <span className="text-foreground/50 text-[10px]">{KIND_LABEL[p.kind]}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-14 shrink-0 text-right">
        {isDealbreaker ? (
          <span
            className="font-data text-[11px] font-semibold"
            style={{ color: "oklch(0.55 0.20 25)" }}
          >
            FAIL
          </span>
        ) : score !== null ? (
          <span
            className="font-data text-[13px] font-medium tabular-nums"
            style={{ color: accent }}
          >
            {score.toFixed(1)}
          </span>
        ) : (
          <span className="text-foreground/40 text-[12px]">—</span>
        )}
      </div>
    </div>
  );
}
